import { IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material"
import { enqueueSnackbar } from "notistack"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCopy } from "@fortawesome/pro-regular-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import type { TranslationKey } from "../../../../localization/translations"

const useStyles = makeStyles()((theme) => ({
    label: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[700],
    },
}))

type Props = Readonly<{
    labelId: TranslationKey
    labelFallback: string
    value: string
    isMultiline?: boolean
}>

/** A labelled read-only value with a copy-to-clipboard action, e.g. a display link or embed code. */
export function CopyLinkField({ labelId, labelFallback, value, isMultiline = false }: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const handleCopy = async () => {
        await navigator.clipboard.writeText(value)
        enqueueSnackbar({ variant: "success", message: l10n.getString("merchant-info-displays-copy-success") })
    }

    return (
        <Stack spacing=".25rem">
            <LocalizedStrict id={labelId}>
                <Typography variant="body2" className={classes.label}>
                    {labelFallback}
                </Typography>
            </LocalizedStrict>
            <Stack direction="row" alignItems="flex-start" spacing=".5rem">
                <TextField
                    value={value}
                    size="small"
                    fullWidth
                    multiline={isMultiline}
                    minRows={isMultiline ? 2 : undefined}
                    maxRows={isMultiline ? 4 : undefined}
                    InputProps={{ readOnly: true }}
                />
                <LocalizedStrict id="merchant-info-displays-copy-tooltip" attrs={{ title: true }}>
                    <Tooltip title="Copy">
                        <IconButton size="small" onClick={handleCopy}>
                            <FontAwesomeIcon icon={faCopy} />
                        </IconButton>
                    </Tooltip>
                </LocalizedStrict>
            </Stack>
        </Stack>
    )
}
